import { useEffect, useRef, useState } from "react";

const works = [
  {
    image:
      "/image/recruit-features1.jpeg",
    title: "ミスマッチを防ぐ、正直な採用",
    description:
      "一般的な採用では、企業をよく見せるために都合の良い情報だけが伝えられてしまい、「入社してみたら違った」というミスマッチが起こることも少なくありません。\nMSKではそうしたギャップをなくすため、しがらみにとらわれないメンバーが直接面談を担当し、仕事内容や働き方についても嘘偽りなくお伝えします。 気になることには納得いくまでお答えし、入社後のミスマッチが起きないよう、徹底したコミュニケーションを大切にしています。",
  },
  {
    image:
      "/image/recruit-features2.webp",
    title: "成長もキャリアも、しっかり伴走サポート",
    description:
      "案件参画後も「任せて終わり」ではなく定期的な1on1を実施し、悩みや不安、キャリアの方向性について一緒に考えながらサポートします。\n各グループごとに定期的な勉強会を行っていますが、不必要なミーティングを強制することはありません。\nまた、興味のある資格は自由に学べる環境が整っており、学ぶ意欲さえあれば会社が全面的にサポート！\n必要な方には目標設定やフィードバックの機会も設けています。",
  },
  {
    image:
      "/image/recruit-features3.jpeg",
    title: "エンジニア目線の会社経営",
    description:
      "当社の経営陣はエンジニア出身者ばかり。長年エンジニアとして活躍した経験があるからこそ、気持ちや大変さが身に沁みてわかります。適切な労働環境なのかを細かく分析・調査しているので、無理な業務を振られる心配もありません。適切な環境で、あなたらしく活躍していきましょう。",
  },
  {
    image:
      "/image/recruit-features4.webp",
    title: "上流工程にも携われます",
    description:
      "おもに首都圏を中心としてWebシステムの開発や運営・保守を手がける株式会社MSK。設計や管理・開発運営までを一貫して携われます。金融を中心に、電気や決済システムなど案件の幅も広いです。上流工程からさまざまな案件を担当できるので、より経験を積むための環境が整っています。",
  },
];

const RecruitFeatures = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const [visibleItems, setVisibleItems] = useState<boolean[]>(
    new Array(works.length).fill(false)
  );
  const [expandedItems, setExpandedItems] = useState<boolean[]>(
    new Array(works.length).fill(false)
  );

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    itemRefs.current.forEach((el, i) => {
      if (!el) return;

      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleItems((prev) => {
              const next = [...prev];
              next[i] = true;
              return next;
            });
            obs.unobserve(el);
          }
        },
        { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
      );

      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  const toggleExpanded = (index: number) => {
    setExpandedItems((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  return (
    <section
      className="py-16 bg-white relative overflow-hidden"
      id="work"
      ref={sectionRef}
    >
      {/* Background decoration text */}
      <div
        className={
          "absolute top-8 left-0 text-[80px] sm:text-[180px] font-bold text-[#89B56B]/5 leading-none select-none pointer-events-none font-['Inter'] tracking-tight anim-bg-text " +
          (isVisible ? "is-visible" : "")
        }
      >
        FEATURES
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <div
          className={
            "text-center mb-16 anim-fade-up " + (isVisible ? "is-visible" : "")
          }
        >
          <p className="text-[#89B56B] text-xs font-semibold tracking-[0.25em] uppercase mb-4 font-['Inter']">
            Features
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#89B56B]">
            MSKの特徴
          </h2>
        </div>

        <div className="flex flex-col gap-16">
          {works.map((work, index) => (
            <article
              key={index}
              ref={(el) => {
                itemRefs.current[index] = el;
              }}
              className={
                "bg-[#F8FAF6] rounded-xl shadow-[0_2px_16px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_32px_rgba(137,181,107,0.12)] transition-all duration-500 overflow-hidden group flex flex-col " +
                (index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse") +
                " " +
                (index % 2 === 0 ? "anim-fade-left" : "anim-fade-right") +
                " " +
                (visibleItems[index] ? "is-visible" : "")
              }
            >
              {/* Image */}
              <div className="lg:w-[45%] w-full h-[240px] sm:h-[280px] lg:h-auto lg:min-h-[320px] flex-shrink-0 overflow-hidden">
                <img
                  src={work.image}
                  alt={work.title}
                  title={`${work.title} - 株式会社MSK`}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                />
              </div>

              {/* Text */}
              <div className="lg:w-[55%] p-8 sm:p-10 lg:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-5">
                  <span className="w-8 h-[2px] bg-[#89B56B] inline-block"></span>
                  <span className="text-[#89B56B] text-xs font-semibold tracking-[0.15em] uppercase font-['Inter']">
                    0{index + 1}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-[#89B56B] mb-5 leading-relaxed">
                  {work.title}
                </h3>

                {/* PC: always show full text */}
                <p className="hidden lg:block text-sm text-gray-500 leading-[2] whitespace-pre-line">
                  {work.description}
                </p>

                {/* Mobile: truncated or expanded */}
                <div className="lg:hidden">
                  <p
                    className={
                      "text-sm text-gray-500 leading-[2] whitespace-pre-line transition-all duration-300 " +
                      (!expandedItems[index]
                        ? "line-clamp-3 overflow-hidden"
                        : "")
                    }
                  >
                    {work.description}
                  </p>
                  <button
                    type="button"
                    onClick={() => toggleExpanded(index)}
                    className="mt-3 flex items-center gap-1 text-xs font-semibold text-[#89B56B] cursor-pointer whitespace-nowrap focus:outline-none"
                  >
                    <span>{expandedItems[index] ? "閉じる" : "続きを読む"}</span>
                    <span
                      className={
                        "w-4 h-4 flex items-center justify-center transition-transform duration-300 " +
                        (expandedItems[index] ? "rotate-180" : "")
                      }
                    >
                      <i className="ri-arrow-down-s-line text-base leading-none"></i>
                    </span>
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RecruitFeatures;
